import { Component, OnInit, OnDestroy, ChangeDetectorRef, Inject } from '@angular/core';
import { docData, Firestore, doc, DocumentReference } from '@angular/fire/firestore';
import { Observable, Subscription } from 'rxjs';
import { CryptoService } from 'src/app/core/firebase/crypto.service';
import { CachingStatus, NftAsset } from 'src/app/core/state/crypto/crypto.state';
import { SOPO_DIALOG_DATA, SopoDialogRef } from 'src/app/sopo-popup/interfaces/dialog';
import { DEFAULT_DISMISS_AFTER_TIME, SopoPopupService } from 'src/app/sopo-popup/service/sopo-popup.service';

type NftSelectorData = {
    isFavorite: boolean,
    nftData$: Observable<NftAsset>,
}
@Component({
    selector: 'app-nft-selector',
    templateUrl: './nft-selector.component.html',
    styleUrls: ['./nft-selector.component.scss'],
    standalone: false
})
export class NftSelectorComponent implements OnInit, OnDestroy {
    nftAsset: NftAsset;
    favoriteText: string;
    showLoadingSpinner = false;
    assetSubscription: Subscription;
    isInGame = false;

    tokenStatusSubscription: Subscription;
    isFavorite: boolean;

    constructor(
        private cryptoService: CryptoService,
        private firestore: Firestore,
        private ref: ChangeDetectorRef,
        private sopoDialogRef: SopoDialogRef,
        @Inject(SOPO_DIALOG_DATA) private data: NftSelectorData,
        private sopoPopupService: SopoPopupService,
    ) { }

    ngOnInit(): void {
        this.assetSubscription = this.data.nftData$.subscribe(asset => this.nftAsset = asset);
        this.favoriteText = this.setFavoritText(this.data.isFavorite);
        this.isFavorite = this.data.isFavorite;
        this.getTokenStatus(this.nftAsset);
     }

    async refreshMetadata(): Promise<void> {
        try {
            await this.cryptoService.refreshNftMetadata(this.nftAsset);
        } catch (err) {
            console.error(err);
        }
    }

    async setFavorite(): Promise<void> {
        try {
            if (this.isFavorite) await this.cryptoService.removeFromFavorites(this.nftAsset);
            else await this.cryptoService.addNftAsFavourite(this.nftAsset);

            this.isFavorite = !this.isFavorite;
            this.favoriteText = this.setFavoritText(this.isFavorite);
            this.ref.markForCheck();
        } catch (err) {
            console.error(err);
            this.sopoPopupService.showAlert('app', {
                message: 'Something went wrong, please try again later.',
                dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
            })
        }
    }

    public async getTokenStatus(nftAsset: NftAsset): Promise<void> {
        const chain = await this.cryptoService.getChain();
        const docId = chain === 'svm' ? nftAsset.collectionTokenId : `${nftAsset.collectionAddress}-${nftAsset.collectionTokenId}`;
        const docReference = doc(this.firestore, `tokenCacheStatus/${docId}`) as DocumentReference<{status: CachingStatus}>;

        this.tokenStatusSubscription = docData(docReference)
            .subscribe(data => {
                if (data && (data.status === CachingStatus.CACHING || data.status === CachingStatus.CACHING_IN_WAITING)) this.showLoadingSpinner = true;
                else this.showLoadingSpinner = false;
                this.ref.markForCheck();
            });
    }

    setFavoritText(isFavorite: boolean): string {
        return isFavorite ? "Unfavorite" : "Favorite";
    }

    close(value?: string): void {
        switch (value) {
            case 'confirm':
                if (this.sopoDialogRef) this.sopoDialogRef.close(value);
                break;
            case 'close':
                if (this.sopoDialogRef) this.sopoDialogRef.close(value);
                break;
            default:
                if (this.sopoDialogRef) this.sopoDialogRef.close();
        }
    }

    ngOnDestroy(): void {
        if (this.tokenStatusSubscription) this.tokenStatusSubscription.unsubscribe();
        if (this.assetSubscription) this.assetSubscription.unsubscribe();
    }
}
